import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import DailySalesTable from "../tables/DailySalesTable";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import moment from "moment";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import { IRegister } from "../../Admin";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number | null;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  allRegisters: IRegister[];
  register_ids: Array<number>;
  sage_50_export_id: number | null;
  quickbooks_export_id: number | null;
  quickbooks_2008_export_id: number | null;
  quickbooks_online_export_id: number | null;
  sales_channels: Array<string>;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function DailySales() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    email: "",
    queue_after_days: 14,
    allRegisters: [],
    register_ids: [],
    sage_50_export_id: null,
    quickbooks_export_id: null,
    quickbooks_2008_export_id: null,
    quickbooks_online_export_id: null,
    sales_channels: [],
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
    void getRegisters();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];

    const childReportsRes = await GetReport({ parent_id: report.id }, true);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      sage_50_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-sage-50-export")?.id ?? null,
      quickbooks_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-export")?.id ?? null,
      quickbooks_2008_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-2008-export")?.id ?? null,
      quickbooks_online_export_id: childReportsRes.data?.find(report => report.code === "daily-sales-quickbooks-online-export")?.id ?? null,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.daily_sales.001")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        register_ids: state.register_ids,
        sales_channels: state.sales_channels,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.daily_sales.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  async function getAccountingSoftwareExport(email: string, export_id: number) {
    if (!export_id) {
      dispatch(showError(t("Cannot find sales export report")));
      return;
    }

    const accountingSoftwareRes = await GetReportRun(
      {
        report_id: export_id,
        type: "raw",
        date_range: state.date_range,
        register_ids: state.register_ids,
        email,
      },
      true,
    );

    if (accountingSoftwareRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.sales_by_accounting_reference.003")));
      return;
    }

    let extension = '.txt';

    if (export_id === state.quickbooks_export_id || export_id === state.quickbooks_2008_export_id) {
      extension = '.iif';
    } else if (export_id === state.quickbooks_online_export_id) {
      extension = '.csv';
    }

    if (!email) {
      downloadCsv(accountingSoftwareRes.data as string, reportFilename() + extension);
    } else {
      dispatch(showSuccess(accountingSoftwareRes.message));
    }
  }

  async function getRegisters() {
    const registerRes = await GetRegisters(null, true);
    if (registerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.004")));
      return;
    }
    setState(prevState => ({ ...prevState, allRegisters: registerRes.data }));
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.daily_sales.003"),
    action: () => {
      if (moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>, nonNumeric?: boolean) {
    const { value, checked, id } = e.target;
    let updateSelectedValues: any[] = [];
    if (state[id]) {
      updateSelectedValues = [...state[id]];
    }
    if (checked) {
      updateSelectedValues.push(nonNumeric ? value : Number(value));
    } else {
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === (nonNumeric ? value : Number(value)));
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleRemoveAll(id: string) {
    setState(prevState => ({ ...prevState, [id]: [] }));
  }

  function handleAddAllRegisters(id: string) {
    const updatedSelectedRegisters: any[] = [];
    state.allRegisters?.forEach((register: any) => {
      updatedSelectedRegisters.push(register?.id);
    });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedRegisters,
    }));
  }

  return (
    <Page
      title={t("secure.facility.reports.pages.daily_sales.004")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.daily_sales.005"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.daily_sales.006"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.daily_sales.007"),
              handler: handlePrint,
              icon: "print",
            },
            ...(state.sage_50_export_id ? [{
              type: "handler",
              label: t("Export for Sage 50"),
              handler: () => {
                void getAccountingSoftwareExport('', state.sage_50_export_id);
              },
              icon: "file-arrow-down",
            } as any] : []),
            ...(state.quickbooks_export_id ? [{
              type: "handler",
              label: t("Export for QuickBooks"),
              handler: () => {
                void getAccountingSoftwareExport('', state.quickbooks_export_id);
              },
              icon: "file-arrow-down",
            } as any] : []),
            ...(state.quickbooks_2008_export_id ? [{
              type: "handler",
              label: t("Export for QuickBooks 2008"),
              handler: () => {
                void getAccountingSoftwareExport('', state.quickbooks_2008_export_id);
              },
              icon: "file-arrow-down",
            } as any] : []),
            ...(state.quickbooks_online_export_id ? [{
              type: "handler",
              label: t("Export for QuickBooks Online"),
              handler: () => {
                void getAccountingSoftwareExport('', state.quickbooks_online_export_id);
              },
              icon: "file-arrow-down",
            } as any] : []),
          ],
        },
      }}
      breadcrumbs={[{ prefix: true, label: t("secure.facility.reports.pages.daily_sales.008"), url: "/admin/report" }]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <FilterDropdown
              label={t("Registers")}
              data={state.allRegisters}
              dataLabelProperty={"title"}
              checkBoxId={"register_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("register_ids")}
              onAddAll={() => handleAddAllRegisters("register_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            <FilterDropdown
              label={t("Sales Channels")}
              data={[
                { id: "pos", title: t("Point of Sale") },
                { id: "online_store", title: t("Online Store") },
                { id: "booking_engine", title: t("Online Booking") },
              ]}
              dataLabelProperty={"title"}
              checkBoxId={"sales_channels"}
              dataValueProperty="id"
              onCheckBoxChange={value => onCheckBoxChange(value, true)}
              onClear={() => handleRemoveAll("sales_channels")}
              onAddAll={() => handleAddAllRegisters("sales_channels")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <DailySalesTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
